"use client"

import posthog from "posthog-js"
import { PostHogProvider } from "posthog-js/react"
import { usePathname, useSearchParams } from "next/navigation"
import {
	Suspense,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { ConsentContext, type ConsentState } from "./use-consent"

const CONSENT_KEY = "onsand_analytics_consent"

function PageviewTracker() {
	const pathname = usePathname()
	const searchParams = useSearchParams()

	useEffect(() => {
		if (pathname && posthog) {
			let url = window.origin + pathname
			const params = searchParams?.toString()
			if (params) {
				url += `?${params}`
			}
			posthog.capture("$pageview", { $current_url: url })
		}
	}, [pathname, searchParams])

	return null
}

function ConsentBanner({
	onAccept,
	onDecline,
}: { onAccept: () => void; onDecline: () => void }) {
	return (
		<div
			style={{
				position: "fixed",
				bottom: "16px",
				right: "16px",
				zIndex: 9999,
				padding: "20px",
				maxWidth: "360px",
				background: "rgb(24 24 27)",
				border: "1px solid rgb(39 39 42)",
				borderRadius: "12px",
				display: "flex",
				flexDirection: "column",
				gap: "16px",
				fontSize: "13px",
				color: "rgb(161 161 170)",
				boxShadow: "0 8px 30px rgba(0,0,0,0.4)",
			}}
		>
			<p style={{ margin: 0, lineHeight: 1.5 }}>
				We use analytics to improve the app. Data is stored in the EU and never
				shared with third parties.
			</p>
			<div style={{ display: "flex", gap: "12px", alignItems: "center", justifyContent: "space-between" }}>
				<button
					type="button"
					onClick={onDecline}
					style={{
						padding: 0,
						background: "transparent",
						border: "none",
						color: "rgb(113 113 122)",
						cursor: "pointer",
						fontSize: "12px",
						textDecoration: "underline",
						textUnderlineOffset: "2px",
					}}
				>
					Decline
				</button>
				<button
					type="button"
					onClick={onAccept}
					style={{
						padding: "10px 24px",
						borderRadius: "6px",
						background: "rgb(255 255 255)",
						border: "none",
						color: "rgb(0 0 0)",
						cursor: "pointer",
						fontSize: "14px",
						fontWeight: 600,
					}}
				>
					Accept
				</button>
			</div>
		</div>
	)
}

export function AnalyticsProvider({
	children,
	autoConsent = false,
}: { children: React.ReactNode; autoConsent?: boolean }) {
	const [consent, setConsent] = useState<ConsentState>(
		autoConsent ? "granted" : "pending",
	)
	// The saved choice lives in localStorage, which the server render can't see.
	// Until it's read, "pending" means "unknown", so the banner must not show.
	const [storedChoiceRead, setStoredChoiceRead] = useState(false)
	const initialized = useRef(false)

	useEffect(() => {
		if (initialized.current) return
		initialized.current = true

		const stored = localStorage.getItem(CONSENT_KEY)
		if (autoConsent || stored === "granted") {
			setConsent("granted")
		} else if (stored === "denied") {
			setConsent("denied")
		}
		setStoredChoiceRead(true)

		const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
		const host = process.env.NEXT_PUBLIC_POSTHOG_HOST
		if (!key || !host) return

		posthog.init(key, {
			api_host: host,
			ui_host: "https://eu.posthog.com",
			opt_out_capturing_by_default: !autoConsent,
			capture_pageview: false,
			capture_pageleave: true,
			persistence: "localStorage+cookie",
		})

		if (autoConsent || stored === "granted") {
			posthog.opt_in_capturing()
		}
	}, [])

	const accept = useCallback(() => {
		localStorage.setItem(CONSENT_KEY, "granted")
		setConsent("granted")
		posthog.opt_in_capturing()
		// Capture the current page since PageviewTracker already fired before opt-in
		posthog.capture("$pageview", { $current_url: window.location.href })
	}, [])

	const decline = useCallback(() => {
		localStorage.setItem(CONSENT_KEY, "denied")
		setConsent("denied")
		posthog.opt_out_capturing()
	}, [])

	return (
		<ConsentContext value={{ consent, accept, decline }}>
			<PostHogProvider client={posthog}>
				<Suspense fallback={null}>
					<PageviewTracker />
				</Suspense>
				{children}
				{storedChoiceRead && consent === "pending" && (
					<ConsentBanner onAccept={accept} onDecline={decline} />
				)}
			</PostHogProvider>
		</ConsentContext>
	)
}
